import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, CalendarDays, ClipboardList, ShieldCheck, TrendingUp, ArrowUpRight,
  UserPlus, CalendarPlus, Upload, PlayCircle, BarChart3, FileText,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell,
} from "recharts";
import { useAuth } from "../context/AuthContext";
import { dashboardApi } from "../lib/api";

function Stat({ icon: Icon, label, value, foot, footClass }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 hover:shadow-lg hover:shadow-slate-200/50 transition-shadow">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-brand-light flex items-center justify-center text-brand-blue shrink-0"><Icon className="w-6 h-6" /></div>
        <div>
          <div className="text-sm text-slate-500">{label}</div>
          <div className="font-display font-extrabold text-3xl text-brand-navy mt-0.5">{value}</div>
        </div>
      </div>
      {foot && <div className={`mt-3 text-sm font-medium ${footClass}`}>{foot}</div>}
    </div>
  );
}

const activityIcon = { employee: UserPlus, document: FileText, organization: ShieldCheck, user: Users };
function timeAgo(ts) {
  try {
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

export default function Dashboard() {
  const { session } = useAuth();
  const nav = useNavigate();
  const firstName = (session?.user?.name || "there").split(" ")[0];
  const [data, setData] = useState({ stats: {}, leaveChart: [], departments: [], recentActivity: [], birthdays: [] });

  useEffect(() => {
    dashboardApi.get().then(setData).catch(() => {});
  }, []);

  const stats = data.stats || {};
  const leaveChartData = data.leaveChart || [];
  const departmentData = data.departments || [];
  const recentActivity = data.recentActivity || [];
  const upcomingBirthdays = data.birthdays || [];

  const quickActions = [
    { icon: UserPlus, label: "Add Employee", onClick: () => nav("/app/employees?new=1") },
    { icon: CalendarPlus, label: "New Leave Request", onClick: () => nav("/app/leave") },
    { icon: Upload, label: "Upload Document", onClick: () => nav("/app/documents") },
    { icon: PlayCircle, label: "Run Payroll", onClick: () => nav("/app/payroll") },
    { icon: BarChart3, label: "View Reports", onClick: () => nav("/app/reports") },
  ];

  return (
    <div className="pt-2 pb-6 animate-fade-up">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-brand-navy">Good morning, {firstName} 👋</h1>
          <p className="text-slate-500 mt-1">Here's what's happening in your organisation today.</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-xl border border-slate-100 px-4 h-11 text-sm font-medium text-slate-700">
          <CalendarDays className="w-4 h-4 text-brand-blue" /> {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
        <Stat icon={Users} label="Total Employees" value={stats.totalEmployees ?? 0} foot={<span className="flex items-center gap-1 text-emerald-600"><TrendingUp className="w-4 h-4" /> {stats.employeesDelta ?? 0} this month</span>} />
        <Stat icon={CalendarDays} label="On Leave Today" value={stats.onLeaveToday ?? 0} foot={<span className="text-brand-blue">View details</span>} footClass="cursor-pointer" />
        <Stat icon={ClipboardList} label="Pending Approvals" value={stats.pendingApprovals ?? 0} foot={<span className="text-brand-blue">View all</span>} footClass="cursor-pointer" />
        <Stat icon={ShieldCheck} label="Compliance Rate" value={`${stats.complianceRate ?? 0}%`} foot={<span className="text-emerald-600">On track</span>} />
      </div>

      {/* Middle row */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Leave overview */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg text-brand-navy">Leave Overview</h3>
            <button className="text-sm font-medium text-brand-blue flex items-center gap-1">View report <ArrowUpRight className="w-4 h-4" /></button>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={leaveChartData} margin={{ left: -20, right: 10, top: 10 }}>
              <defs>
                <linearGradient id="leaveGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1A56DB" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#1A56DB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Area type="monotone" dataKey="value" stroke="#1A56DB" strokeWidth={2.5} fill="url(#leaveGrad)" dot={{ r: 3, fill: "#1A56DB" }} activeDot={{ r: 5 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Recent activity */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg text-brand-navy">Recent Activity</h3>
            <button className="text-sm font-medium text-brand-blue">View all</button>
          </div>
          <div className="space-y-4">
            {recentActivity.length === 0 && <p className="text-sm text-slate-400">No activity yet.</p>}
            {recentActivity.map((a) => {
              const Icon = activityIcon[a.entity] || FileText;
              return (
                <div key={a.id} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-brand-light flex items-center justify-center text-brand-blue shrink-0"><Icon className="w-4 h-4" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-brand-navy">{a.summary}</div>
                    <div className="text-xs text-slate-500 truncate">{a.actor}</div>
                  </div>
                  <div className="text-xs text-slate-400 whitespace-nowrap">{timeAgo(a.timestamp)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Quick actions */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h3 className="font-display font-bold text-lg text-brand-navy mb-4">Quick Actions</h3>
          <div className="space-y-2.5">
            {quickActions.map((q) => (
              <button key={q.label} onClick={q.onClick} className="w-full flex items-center gap-3 px-4 h-12 rounded-xl border border-slate-100 hover:border-brand-blue/40 hover:bg-brand-light/40 transition-colors text-sm font-medium text-slate-700">
                <q.icon className="w-5 h-5 text-brand-blue" /> {q.label}
              </button>
            ))}
          </div>
        </div>

        {/* Birthdays */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display font-bold text-lg text-brand-navy">Upcoming Birthdays</h3>
            <button className="text-sm font-medium text-brand-blue">View all</button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {upcomingBirthdays.length === 0 && <p className="text-sm text-slate-400 col-span-2">No upcoming birthdays.</p>}
            {upcomingBirthdays.map((b, i) => {
              const [mm, dd] = (b.md || "").split("-");
              const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
              const dateLabel = mm ? `${months[parseInt(mm, 10) - 1]} ${parseInt(dd, 10)}` : "";
              const initials = b.name.split(" ").map((n) => n[0]).slice(0, 2).join("");
              return (
                <div key={i} className="text-center">
                  {b.avatar ? (
                    <img src={b.avatar} alt={b.name} className="w-14 h-14 rounded-full object-cover mx-auto ring-2 ring-brand-light" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-brand-light text-brand-blue flex items-center justify-center font-semibold mx-auto">{initials}</div>
                  )}
                  <div className="text-sm font-semibold text-brand-navy mt-2 leading-tight">{b.name}</div>
                  <div className="text-xs text-slate-500">{dateLabel}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Department overview */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-bold text-lg text-brand-navy">Department Overview</h3>
            <button className="text-sm font-medium text-brand-blue">View report</button>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-[150px] h-[160px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={departmentData} dataKey="value" innerRadius={40} outerRadius={64} paddingAngle={2} stroke="none">
                  {departmentData.map((d) => (<Cell key={d.name} fill={d.color} />))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2">
              {departmentData.map((d) => (
                <div key={d.name} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-slate-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />{d.name}</span>
                  <span className="font-semibold text-brand-navy">{d.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
